import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Typography,
  Upload,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  CameraOutlined,
  CheckOutlined,
  CloseOutlined,
  CreditCardOutlined,
  DownOutlined,
  HeartOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  RightOutlined,
  SaveOutlined,
  SearchOutlined,
  UpOutlined,
  WalletOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import useWorkspaceSubscriptionFeatures from "../../hooks/useWorkspaceSubscriptionFeatures";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { useFileUpload } from "../../hooks/useFileUpload";
import { createTaskRequest } from "../../services/olitaskTaskService";
import { olreadiaService } from "../../services/olreadiaService";
import { addressFieldsFromPlaceDetails } from "../../utils/googlePlaces";
import apiClient from "../../utils/apiClient";
import { PAYMENT_SERVICE_BASE_URL } from "../../constants";
import {
  FEDERAL_MINIMUM_WAGE,
  getSuggestedTokenAmount,
  stateMinimumWages,
} from "../../utils/minimumWages";

const { Text, Title } = Typography;

const CARE_TASK_OUT_OF_APP_PAYMENT_FEE = 2.99;
const DEFAULT_CURRENCY = "USD";
const MOBILE_CLIENT_APP = "OLITASK_MOBILE_UI";
const CHARGES_APP = "OLITASK_MOBILE_UI";
const CHARGES_STAGE = "OLITASK_MOBILE_UI_ALL";
const CHARGE_TARGET = "CHECKOUT";

const CARE_TASK_FLOW_STEPS = [
  { key: "services", title: "Care Services" },
  { key: "description", title: "Describe the care shift" },
  { key: "location", title: "Care Location" },
  { key: "schedulePay", title: "Schedule & Pay" },
  { key: "paymentChoice", title: "Care Giver Payment" },
  { key: "review", title: "Confirm request" },
];

const serviceGroup = (title, icon, rank, services) => ({
  title,
  icon,
  rank,
  services: services.map(([id, itemTitle, brief, description]) => ({
    id,
    title: itemTitle,
    brief,
    description,
    category: title,
    icon,
  })),
});

const CARE_SERVICE_GROUPS = [
  serviceGroup("Personal Care Support", "body-outline", 1, [
    ["BATHING_ASSISTANCE", "Bathing assistance", "Bathing Help", "Support with bathing routines and bathroom safety."],
    ["DRESSING_GROOMING", "Dressing and grooming", "Dressing & Grooming", "Help getting dressed, grooming, and preparing for the day."],
    ["TOILETING_SUPPORT", "Toileting support", "Toileting Support", "Assistance getting to and from the restroom."],
    ["INCONTINENCE_CARE", "Incontinence care", "Incontinence Care", "Non-medical personal support related to incontinence needs."],
    ["ORAL_HYGIENE", "Oral hygiene", "Oral Hygiene", "Reminders and light support with oral hygiene routines."],
    ["SKIN_CARE_SUPPORT", "Skin care support", "Skin Care Support", "Non-medical support with approved skin care routines."],
    ["BED_CHAIR_TRANSFER", "Help moving in and out of bed or chair", "Bed/Chair Help", "Support with safe movement in and out of bed or a chair."],
  ]),
  serviceGroup("Daily Living Assistance", "home-outline", 1, [
    ["MEAL_PREPARATION", "Meal preparation", "Meal Prep", "Preparing meals, snacks, and simple kitchen support."],
    ["FEEDING_ASSISTANCE", "Feeding assistance", "Feeding Assistance", "Non-medical help during meals when appropriate."],
    ["LIGHT_HOUSEKEEPING", "Light housekeeping", "Light Housekeeping", "Light cleaning, tidying, dishes, and safe living area support."],
    ["LAUNDRY", "Laundry", "Laundry", "Washing, drying, folding, and putting away laundry."],
    ["CHANGING_BED_LINENS", "Changing bed linens", "Bed Linens", "Changing and organizing bed linens."],
    ["ORGANIZING_ITEMS", "Organizing personal items", "Personal Organization", "Organizing personal items and daily essentials."],
    ["SUPPLY_SUPPORT", "Grocery or household supply support", "Supply Support", "Help with grocery or household supply needs."],
  ]),
  serviceGroup("Mobility and Safety", "walk-outline", 4, [
    ["WALKING_ASSISTANCE", "Walking assistance", "Walking Help", "Support while walking around home, facility, or nearby areas."],
    ["WHEELCHAIR_SUPPORT", "Wheelchair support", "Wheelchair Support", "Support with wheelchair movement and positioning."],
    ["TRANSFER_ASSISTANCE", "Transfer assistance", "Transfer Help", "Support moving between seated, standing, bed, or chair positions."],
    ["FALL_PREVENTION", "Fall prevention support", "Fall Prevention", "Safety-focused support to reduce fall risk."],
    ["ESCORTING_HOME_FACILITY", "Escorting around the home or facility", "Home/Facility Escort", "Escorting around the home, facility, hallways, or common areas."],
    ["STAIRS_MOBILITY_DEVICES", "Helping with stairs or mobility devices", "Stairs & Devices", "Help using stairs or approved mobility devices."],
  ]),
  serviceGroup("Companionship", "people-outline", 7, [
    ["CONVERSATION_SUPPORT", "Conversation and emotional support", "Conversation", "Conversation, emotional support, and friendly presence."],
    ["READING_TOGETHER", "Reading together", "Reading Together", "Reading books, mail, news, or other materials together."],
    ["GAMES_PUZZLES", "Playing games or puzzles", "Games & Puzzles", "Playing games, cards, puzzles, or light activities."],
    ["WATCHING_SHOWS", "Watching shows together", "Shows Together", "Watching shows, movies, or events together."],
    ["SOCIAL_VISIT", "Social visit support", "Social Visit", "Supportive social visits at home or in a facility."],
    ["LONELINESS_SUPPORT", "Reducing loneliness", "Loneliness Support", "Regular companionship to help reduce isolation."],
  ]),
  serviceGroup("Health-Related Non-Medical Support", "heart-outline", 5, [
    ["MEDICATION_REMINDERS", "Medication reminders", "Medication Reminders", "Reminder-only support. Care Givers do not administer medication."],
    ["APPOINTMENT_REMINDERS", "Appointment reminders", "Appointment Reminders", "Reminders for upcoming appointments and care routines."],
    ["WELLBEING_MONITORING", "Monitoring general wellbeing", "Wellbeing Check", "General observation and check-ins, with concerns reported to family or staff."],
    ["REPORTING_CONCERNS", "Reporting concerns to family or staff", "Report Concerns", "Share observed concerns with family or facility staff."],
    ["HYDRATION_MEALS", "Encouraging hydration and meals", "Hydration & Meals", "Encouragement and reminders for hydration and meals."],
    ["EXERCISE_REMINDERS", "Exercise reminders, if approved by caregiver or care plan", "Exercise Reminders", "Reminders for approved exercises only."],
  ]),
  serviceGroup("Transportation and Errands", "car-outline", 6, [
    ["APPOINTMENT_RIDES", "Rides to appointments", "Appointment Rides", "Transportation to and from non-emergency appointments."],
    ["PHARMACY_PICKUP", "Pharmacy pickup", "Pharmacy Pickup", "Pickup support for pharmacy items."],
    ["GROCERY_PICKUP", "Grocery pickup", "Grocery Pickup", "Pickup support for groceries or supplies."],
    ["COMMUNITY_ESCORT", "Escort to church, community events, or family visits", "Community Escort", "Escort support for church, events, or family visits."],
    ["APPOINTMENT_CHECKIN", "Help with check-in at appointments", "Appointment Check-in", "Help navigating arrival and check-in at appointments."],
  ]),
  serviceGroup("Family Caregiver Support", "shield-checkmark-outline", 1, [
    ["RESPITE_CARE", "Respite care", "Respite Care", "Short support blocks so family caregivers can rest or run errands."],
    ["SITTING_WITH_LOVED_ONE", "Sitting with a loved one while family rests", "Family Rest Support", "Sit with a loved one while family takes a break."],
    ["HOSPITAL_DISCHARGE_RECOVERY", "Helping during recovery after hospital discharge", "Recovery Support", "Non-medical support during recovery after hospital discharge."],
    ["OVERNIGHT_WATCH", "Overnight watch or supervision", "Overnight Watch", "Overnight presence, supervision, and non-medical support."],
    ["FAMILY_EVENTS_TRAVEL", "Support during family events or travel", "Events & Travel Support", "Care support during family events or travel."],
  ]),
  serviceGroup("Special Needs Support", "accessibility-outline", 2, [
    ["DISABILITY_COMPANION_CARE", "Companion care for people with disabilities", "Disability Companion", "Companion support for people with disabilities."],
    ["ROUTINE_SUPPORT", "Help with routines", "Routine Support", "Support following daily routines and structure."],
    ["SENSORY_FRIENDLY_SUPPORT", "Sensory-friendly support", "Sensory-Friendly", "Calm, sensory-aware support based on preferences."],
    ["SUPERVISION_SAFETY", "Supervision and safety support", "Safety Supervision", "Supervision and safety-focused companion support."],
    ["DAILY_ACTIVITY_ASSISTANCE", "Assistance with daily activities", "Daily Activities", "Support with daily activities and non-medical routines."],
  ]),
].sort((a, b) => a.rank - b.rank || a.title.localeCompare(b.title));

const CARE_SERVICES = CARE_SERVICE_GROUPS.flatMap((group) => group.services);
const INITIAL_VISIBLE_CARE_SERVICE_GROUPS = 4;

const compactPayload = (object) =>
  Object.fromEntries(
    Object.entries(object || {}).filter(([, value]) => {
      if (value === undefined || value === "") return false;
      if (Array.isArray(value)) return true;
      if (value && typeof value === "object") return Object.keys(compactPayload(value)).length > 0;
      return true;
    }),
  );

const clean = (value) => (typeof value === "string" ? value.trim() || undefined : value);
const toDate = (value) => value?.toDate?.() || value?.$d || value || null;
const addMinutes = (date, minutes) => new Date(date.getTime() + minutes * 60000);
const formatMoney = (value, currency = DEFAULT_CURRENCY) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return `${currency} 0.00`;
  return `${currency} ${numeric.toFixed(2)}`;
};
const moneyNumber = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return 0;
  return Math.round((numeric + Number.EPSILON) * 100) / 100;
};
const normalizeChargeText = (value) => String(value || "").trim().toUpperCase();
const chargeSpecificityScore = (config, location) => {
  const targetCountry = normalizeChargeText(location?.country);
  const targetState = normalizeChargeText(location?.state);
  const targetCounty = normalizeChargeText(location?.county);
  const targetCity = normalizeChargeText(location?.city);
  const configCountry = normalizeChargeText(config?.country);
  const configState = normalizeChargeText(config?.state);
  const configCounty = normalizeChargeText(config?.county);
  const configCity = normalizeChargeText(config?.city);

  if (configCountry && targetCountry && configCountry !== targetCountry) return -1;
  if (configState && targetState && configState !== targetState) return -1;
  if (configCounty && targetCounty && configCounty !== targetCounty) return -1;
  if (configCity && targetCity && configCity !== targetCity) return -1;

  return (configCountry ? 1 : 0) + (configState ? 2 : 0) + (configCounty ? 4 : 0) + (configCity ? 8 : 0);
};
const normalizeChargeConfigs = (charges, location) => {
  const byCode = new Map();
  (charges || []).forEach((config) => {
    if (!config?.code) return;
    byCode.set(config.code, [...(byCode.get(config.code) || []), config]);
  });

  return Array.from(byCode.values())
    .map((configs) =>
      [...configs]
        .map((config) => ({ config, score: chargeSpecificityScore(config, location) }))
        .filter((entry) => entry.score >= 0)
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          const versionDelta = Number(b.config?.versionNumber || 0) - Number(a.config?.versionNumber || 0);
          if (versionDelta) return versionDelta;
          return Date.parse(b.config?.effectiveFrom || "") - Date.parse(a.config?.effectiveFrom || "");
        })[0]?.config,
    )
    .filter(Boolean)
    .sort((a, b) => {
      const orderDelta = Number(a?.displayOrder ?? 9999) - Number(b?.displayOrder ?? 9999);
      if (orderDelta) return orderDelta;
      return String(a?.code || "").localeCompare(String(b?.code || ""));
    });
};
const fetchEffectiveCharges = async ({ currency, country, state, county, city }) => {
  const query = new URLSearchParams({
    app: CHARGES_APP,
    stages: CHARGES_STAGE,
    chargeTarget: CHARGE_TARGET,
    currency,
    country,
  });
  if (CHARGE_TARGET) query.set("state", CHARGE_TARGET);
  if (state) query.set("state", state);
  if (county) query.set("county", county);
  if (city) query.set("city", city);

  const pageSize = 50;
  const all = [];
  for (let page = 0; page <= 50; page += 1) {
    const response = await apiClient.get(`${PAYMENT_SERVICE_BASE_URL}/api/v1/charges/effective/paged?${query.toString()}&page=${page}&size=${pageSize}`);
    const data = response?.data || {};
    all.push(...(Array.isArray(data.content) ? data.content : []));
    if (data.last !== false) break;
  }
  return all.filter((config) => normalizeChargeText(config?.status) === "ACTIVE");
};
const computeTaskCharges = async (amount, { currency, country, state, county, city }) => {
  const tokenAmount = moneyNumber(amount);
  const tipAmount = 0;
  const appliedCreditsTotal = 0;
  const feeTaxBase = tokenAmount + tipAmount;
  const chargeConfigs = await fetchEffectiveCharges({ currency, country, state, county, city });
  const chargeLines = normalizeChargeConfigs(chargeConfigs, { country, state, county, city }).map((config) => {
    const applyOn = normalizeChargeText(config?.applyOn);
    const valueType = normalizeChargeText(config?.valueType);
    const base = applyOn === "TOKEN_ONLY" ? tokenAmount : applyOn === "TIP_ONLY" ? tipAmount : feeTaxBase;
    const amountValue = valueType === "PERCENTAGE"
      ? base * Number(config?.value || 0)
      : valueType === "FIXED"
        ? Number(config?.value || 0)
        : 0;
    return {
      chargeId: config?.id,
      code: config?.code,
      name: config?.name,
      description: config?.description ?? null,
      category: config?.category,
      valueType: config?.valueType,
      rate: valueType === "PERCENTAGE" ? config?.value : undefined,
      fixedAmount: valueType === "FIXED" ? config?.value : undefined,
      applyOn: config?.applyOn,
      amount: moneyNumber(amountValue),
      displayOrder: config?.displayOrder ?? null,
      country: config?.country ?? null,
      state: config?.state ?? null,
      county: config?.county ?? null,
      city: config?.city ?? null,
    };
  });
  const totalCharges = chargeLines.reduce((sum, line) => sum + Number(line.amount || 0), 0);
  return {
    currency,
    units: "DOLLAR",
    tokenAmount,
    tipAmount,
    useCredits: false,
    appliedCreditsTotal,
    cashPortion: tokenAmount,
    feeTaxBase: moneyNumber(feeTaxBase),
    country,
    state,
    county,
    city,
    chargeLines,
    totalCharge: moneyNumber(feeTaxBase + totalCharges),
  };
};
const formatDurationText = (minutes) => {
  const total = Number(minutes || 0);
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours && mins) return `${hours} hr ${mins} min`;
  if (hours) return `${hours} hr`;
  return `${mins} min`;
};
const locationInfoSource = (info) => {
  if (!info) return "";
  if (typeof info !== "object") return info;
  return {
    ...(info.locationInformation && typeof info.locationInformation === "object" ? info.locationInformation : {}),
    ...(info.address && typeof info.address === "object" ? info.address : {}),
    ...(info.location && typeof info.location === "object" ? info.location : {}),
    ...info,
  };
};
const looksLikeStreetAddress = (value) => {
  const text = clean(value);
  return Boolean(text && (text.includes(",") || /\d/.test(text)));
};
const addressFromLocationInfo = (info) => {
  if (!info) return "";
  if (typeof info === "string") return info;
  const source = locationInfoSource(info);
  const formattedAddress = clean(source.formattedAddress || source.displayAddress || source.fullAddress);
  if (formattedAddress) return formattedAddress;
  return [
    typeof source.address === "string" ? source.address : "",
    source.addressLine1 || source.streetAddress || source.street || source.line1,
    source.addressLine2 || source.line2,
    source.city,
    source.county,
    source.state || source.stateCode,
    source.zipCode || source.postalCode,
    source.country || source.countryCode,
  ]
    .map(clean)
    .filter(Boolean)
    .join(", ");
};
const hasStreetAddress = (info) => {
  if (!info) return false;
  if (typeof info === "string") return looksLikeStreetAddress(info);
  const source = locationInfoSource(info);
  return Boolean(
    clean(source.addressLine1 || source.streetAddress || source.street || source.line1) ||
      looksLikeStreetAddress(source.formattedAddress || source.displayAddress || source.fullAddress) ||
      (typeof source.address === "string" && looksLikeStreetAddress(source.address)),
  );
};
const pageItems = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.content)) return value.content;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};
const coordsFromLocationInfo = (info) => {
  if (!info || typeof info !== "object") return null;
  const source = locationInfoSource(info);
  const latitude = Number(source.latitude ?? source.lat);
  const longitude = Number(source.longitude ?? source.lng ?? source.lon);
  return Number.isFinite(latitude) && Number.isFinite(longitude) ? { latitude, longitude } : null;
};
const careEntityLocationPayload = (info) => {
  if (!info || typeof info !== "object") return undefined;
  const source = locationInfoSource(info);
  return {
    addressLine1: source.addressLine1 || source.streetAddress || source.street || source.line1,
    addressLine2: source.addressLine2 || source.line2,
    city: source.city,
    county: source.county,
    state: source.state || source.stateCode,
    zipCode: source.zipCode || source.postalCode,
    country: source.country || source.countryCode,
    latitude: source.latitude ?? source.lat,
    longitude: source.longitude ?? source.lng ?? source.lon,
  };
};
const organizationLocationTypeCode = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.code || value.value || value.key || "";
};
const organizationLocationTypeId = (value) => {
  if (!value || typeof value === "string") return "";
  return value.id || "";
};
const organizationLocationTypeLabel = (value) => {
  if (!value) return "Location type not set";
  if (typeof value !== "string") {
    return (
      value.label ||
      value.name ||
      value.displayName ||
      value.title ||
      organizationLocationTypeLabel(value.code || value.value || value.key)
    );
  }
  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};
const organizationLocationTypePayload = (type, typeId) => {
  return {
    id: organizationLocationTypeId(type) || typeId || undefined,
    code: organizationLocationTypeCode(type) || (typeof type === "string" ? type : undefined),
    name: organizationLocationTypeLabel(type),
  };
};
const organizationLocationsOf = (organization) =>
  organization?.businessLocations || organization?.organizationLocations || organization?.locations || [];
const organizationContactEmail = (organization) =>
  organization?.email || organization?.primaryEmail || organization?.contactEmail || organization?.contactInformation?.email;
const organizationContactPhone = (organization) =>
  organization?.phone || organization?.primaryPhone || organization?.contactPhone || organization?.contactInformation?.phone;
const formatEntityType = (value) =>
  organizationLocationTypeLabel(value || "OTHER");
const locationPartsFromInfo = (info) => {
  if (!info || typeof info !== "object") return {};
  return {
    country: info.country || info.countryCode || "",
    state: info.state || info.stateCode || "",
    county: info.county || "",
    city: info.city || "",
  };
};
const organizationLocationCoords = (entity, line) => {
  const lineLocation = line?.locationInformation;
  const hasLineCoords = lineLocation?.latitude != null && lineLocation?.longitude != null;
  const location = hasLineCoords ? lineLocation : entity?.locationInformation || {};
  if (location.latitude == null || location.longitude == null) return null;
  return {
    latitude: Number(location.latitude),
    longitude: Number(location.longitude),
  };
};
const getLocationPart = (value, key) =>
  String(
    value?.locationInformation?.[key] ||
      value?.address?.[key] ||
      value?.location?.[key] ||
      value?.[key] ||
      "",
  )
    .trim()
    .toUpperCase();
const getLocationState = (value) => getLocationPart(value, "state");

function RequiredLabel({ children }) {
  return (
    <Text className="olitask-required-label">
      {children} <span>*</span>
    </Text>
  );
}

function FlowSection({ title, subtitle, children }) {
  return (
    <Card
      className="olitask-mobile-card care-task-flow-card"
      title={
        <span className="olitask-section-heading">
          <Text className="olitask-section-title">{title}</Text>
          {subtitle ? <Text className="olitask-section-subtitle">{subtitle}</Text> : null}
        </span>
      }
      bodyStyle={{ padding: 14 }}
      headStyle={{ padding: "0 14px" }}
    >
      {children}
    </Card>
  );
}

function SummaryRow({ label, value, strong }) {
  return (
    <div className="care-task-summary-row">
      <Text type="secondary">{label}</Text>
      <Text strong={strong}>{value || "Not added"}</Text>
    </div>
  );
}

export default function ShiftFillerCreatePage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { user } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId, selectedWorkspaceUse } = useWorkspace();
  const { uploadFile, uploading: uploadingFile } = useFileUpload();
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const { tier: workspaceSubscriptionTier } = useWorkspaceSubscriptionFeatures();
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);
  const [serviceQuery, setServiceQuery] = useState("");
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [expandedServiceGroups, setExpandedServiceGroups] = useState(
    CARE_SERVICE_GROUPS.slice(0, 2).map((group) => group.title),
  );
  const [selectedSupportExpanded, setSelectedSupportExpanded] = useState(true);
  const [showIntroBadge, setShowIntroBadge] = useState(true);
  const [showCareWarningBadge, setShowCareWarningBadge] = useState(true);
  const [showPhotoPrivacyWarning, setShowPhotoPrivacyWarning] = useState(true);
  const [photoList, setPhotoList] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");
  const [selectedOrganizationLocationId, setSelectedOrganizationLocationId] = useState("");
  const [careLocationMode, setCareLocationMode] = useState("REGISTERED");
  const [locationInput, setLocationInput] = useState("");
  const [coords, setCoords] = useState(null);
  const [manualLocationParts, setManualLocationParts] = useState({});
  const [payHelperInApp, setPayHelperInApp] = useState(true);

  const watchedValues = Form.useWatch([], { form, preserve: true }) || {};
  const workspaceId = selectedWorkspaceId || selectedWorkspace?.id;
  const effectiveOrganizationId = selectedWorkspace?.organizationId || selectedWorkspace?.organization?.id || "";
  const canRequestCareHelp = Boolean(workspaceId && effectiveOrganizationId);
  const isHelperContext = String(selectedWorkspaceUse || "").toUpperCase() === "HELPER";
  const googleMapsApiKey =
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    "";

  const selectedOrganization = useMemo(
    () => organizations.find((organization) => String(organization.id) === String(selectedOrganizationId)) || null,
    [organizations, selectedOrganizationId],
  );
  const selectedOrganizationLocations = useMemo(
    () => organizationLocationsOf(selectedOrganization),
    [selectedOrganization],
  );
  const selectedOrganizationLocation = useMemo(
    () =>
      selectedOrganizationLocations.find((location) => String(location.id) === String(selectedOrganizationLocationId)) ||
      null,
    [selectedOrganizationLocationId, selectedOrganizationLocations],
  );

  const filteredServiceGroups = useMemo(() => {
    const query = serviceQuery.trim().toLowerCase();
    if (!query) return CARE_SERVICE_GROUPS;
    return CARE_SERVICE_GROUPS.map((group) => ({
      ...group,
      services: group.services.filter((service) =>
        [service.title, service.brief, service.description, service.category].some((value) =>
          String(value || "").toLowerCase().includes(query),
        ),
      ),
    })).filter((group) => group.services.length > 0);
  }, [serviceQuery]);

  const visibleServiceGroups = showAllGroups
    ? filteredServiceGroups
    : filteredServiceGroups.slice(0, INITIAL_VISIBLE_CARE_SERVICE_GROUPS);
  const selectedCareServices = useMemo(
    () => CARE_SERVICES.filter((service) => selectedServices.includes(service.id)),
    [selectedServices],
  );
  const startAt = toDate(watchedValues.startAt) || dayjs().add(2, "hour").toDate();
  const endAt = toDate(watchedValues.endAt) || dayjs().add(4, "hour").toDate();
  const scheduleMode = watchedValues.scheduleMode || "IMMEDIATE";
  const scheduledDurationDeltaMinutes = Math.round((endAt.getTime() - startAt.getTime()) / 60000);
  const scheduledDurationMinutes = Math.max(0, scheduledDurationDeltaMinutes);
  const scheduledRangeInvalid = scheduleMode === "SCHEDULED" && scheduledDurationDeltaMinutes <= 0;
  const immediateDurationMinutes =
    Number(watchedValues.durationHours || 0) * 60 +
    Number(watchedValues.durationMinutes ?? 0);
  const durationMinutes = scheduleMode === "IMMEDIATE" ? immediateDurationMinutes : scheduledDurationMinutes;
  const numericPay = Number(watchedValues.payRate || 0);
  const payType = watchedValues.payType || "HOURLY";
  const estimatedPay = payType === "HOURLY" ? numericPay * (durationMinutes / 60) : numericPay;
  const durationHours = durationMinutes / 60;
  const averageHourlyPay = durationHours > 0 ? estimatedPay / durationHours : 0;
  const hasTaskFeeWaiver = ["STANDARD", "PREMIUM"].includes(String(workspaceSubscriptionTier || "").toUpperCase());
  const outOfAppPlatformFee = hasTaskFeeWaiver ? 0 : CARE_TASK_OUT_OF_APP_PAYMENT_FEE;
  const safeChargeBase = payHelperInApp ? estimatedPay : outOfAppPlatformFee;
  const feesTotal = payHelperInApp && safeChargeBase > 0 ? Math.max(1.25, safeChargeBase * 0.08) : outOfAppPlatformFee;
  const taxesTotal = 0;
  const chargeableAmount = payHelperInApp ? safeChargeBase + feesTotal + taxesTotal : outOfAppPlatformFee;
  const receivableAmount = payHelperInApp ? estimatedPay : 0;
  const activeStep = CARE_TASK_FLOW_STEPS[activeStepIndex] || CARE_TASK_FLOW_STEPS[0];
  const isReviewStep = activeStep.key === "review";
  const servicesSummary = selectedCareServices.map((service) => service.brief).join(", ") || "None selected";
  const photoSummary = photoList.length === 1 ? "1 photo" : `${photoList.length} photos`;
  const scheduleSummary =
    scheduleMode === "IMMEDIATE"
      ? `Immediate - ${formatDurationText(durationMinutes)}`
      : `${dayjs(startAt).format("MMM D, YYYY h:mm A")} - ${dayjs(endAt).format("MMM D, YYYY h:mm A")}`;
  const selectedCareLocationAddress =
    careLocationMode === "REGISTERED"
      ? addressFromLocationInfo(selectedOrganizationLocation)
      : locationInput;
  const selectedCareLocationCoords =
    careLocationMode === "REGISTERED"
      ? organizationLocationCoords(selectedOrganization, selectedOrganizationLocation)
      : coords;
  const selectedState =
    careLocationMode === "MANUAL"
      ? String(manualLocationParts.state || "").trim().toUpperCase()
      : getLocationState(selectedOrganizationLocation) || getLocationState(selectedOrganization);
  const selectedCountry = (
    careLocationMode === "MANUAL"
      ? manualLocationParts.country || "US"
      : getLocationPart(selectedOrganizationLocation, "country") || getLocationPart(selectedOrganization, "country") || "US"
  ).toUpperCase();
  const selectedCounty =
    careLocationMode === "MANUAL"
      ? String(manualLocationParts.county || "").trim().toUpperCase()
      : getLocationPart(selectedOrganizationLocation, "county") || getLocationPart(selectedOrganization, "county");
  const selectedCity =
    careLocationMode === "MANUAL"
      ? String(manualLocationParts.city || "").trim().toUpperCase()
      : getLocationPart(selectedOrganizationLocation, "city") || getLocationPart(selectedOrganization, "city");
  const wageStateCode = String(selectedState || "").toUpperCase();
  const minimumWageRate = stateMinimumWages[wageStateCode] ?? FEDERAL_MINIMUM_WAGE;
  const suggestedAmount = durationMinutes > 0 ? getSuggestedTokenAmount(wageStateCode, durationMinutes) : 0;
  const minimumWageLabel =
    wageStateCode && minimumWageRate !== FEDERAL_MINIMUM_WAGE
      ? `${wageStateCode}'s minimum wage for the selected organization location`
      : "The federal minimum wage";

  useEffect(() => {
    let cancelled = false;
    const loadWorkspaceOrganization = async () => {
      if (!effectiveOrganizationId) {
        setOrganizations([]);
        setSelectedOrganizationId("");
        return;
      }
      setOrganizationsLoading(true);
      try {
        const [organizationResult, locationsResult] = await Promise.allSettled([
          olreadiaService.organizations.get(effectiveOrganizationId, {
            wid: workspaceId,
            refresh: true,
          }),
          olreadiaService.organizations.locations.list(effectiveOrganizationId, {
            wid: workspaceId,
            page: 0,
            size: 100,
          }),
        ]);
        if (organizationResult.status !== "fulfilled") throw organizationResult.reason;
        if (cancelled) return;
        const organization = organizationResult.value;
        const fullLocations =
          locationsResult.status === "fulfilled" ? pageItems(locationsResult.value) : [];
        const locations = organizationLocationsOf(organization);
        const nextOrganization = {
          ...organization,
          businessLocations: fullLocations.length ? fullLocations : locations,
        };
        setOrganizations([nextOrganization]);
        setSelectedOrganizationId(organization?.id || effectiveOrganizationId);
        const firstLocation = (fullLocations.length ? fullLocations : locations)[0];
        if (firstLocation?.id) {
          setSelectedOrganizationLocationId((current) => current || firstLocation.id);
          const nextCoords = coordsFromLocationInfo(firstLocation);
          if (nextCoords) setCoords(nextCoords);
        }
      } catch (error) {
        if (!cancelled) {
          setOrganizations([]);
          setSubmitError(error?.response?.data?.message || error?.message || "Unable to load the care facility for this workspace.");
        }
      } finally {
        if (!cancelled) setOrganizationsLoading(false);
      }
    };
    loadWorkspaceOrganization();
    return () => {
      cancelled = true;
    };
  }, [effectiveOrganizationId, workspaceId]);

  const toggleService = (id) => {
    setSelectedServices((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const toggleGroup = (services) => {
    const ids = services.map((service) => service.id);
    const allSelected = ids.every((id) => selectedServices.includes(id));
    setSelectedServices((current) => {
      if (allSelected) return current.filter((id) => !ids.includes(id));
      return Array.from(new Set([...current, ...ids]));
    });
  };

  const toggleServiceGroup = (title) => {
    setExpandedServiceGroups((current) =>
      current.includes(title) ? current.filter((item) => item !== title) : [...current, title],
    );
  };

  const clearSelectedServices = () => {
    setSelectedServices([]);
    setSelectedSupportExpanded(true);
  };

  const validateStep = useCallback(
    (stepKey) => {
      if (isHelperContext) return "Switch to Requester context to submit a shift request.";
      if (!canRequestCareHelp) return "Switch to an organizational requester workspace before creating a care shift request.";
      if (stepKey === "services" && selectedServices.length === 0) return "Choose at least one care support service.";
      if (stepKey === "location") {
        if (!selectedOrganization?.id) return "Select the registered care facility for this request.";
        if (careLocationMode === "REGISTERED" && selectedOrganizationLocations.length > 0 && !selectedOrganizationLocationId) {
          return "Select the care facility location.";
        }
        if (careLocationMode === "REGISTERED" && selectedOrganizationLocation && !hasStreetAddress(selectedOrganizationLocation)) {
          return "Select a registered location with a full street address, or enter the care address manually.";
        }
        if (!selectedCareLocationAddress) return "Enter where the care support should happen.";
        if (careLocationMode === "MANUAL" && googleMapsApiKey && !coords) return "Select a location suggestion from Google Maps.";
      }
      if (stepKey === "schedulePay") {
        if (scheduleMode === "SCHEDULED" && endAt <= startAt) return "End date/time must be after start date/time.";
        if (durationMinutes <= 0) return "Select how long the care request should take.";
        if (!Number.isFinite(numericPay) || numericPay <= 0) return "Enter a valid pay rate or lump sum.";
      }
      return "";
    },
    [
      canRequestCareHelp,
      careLocationMode,
      coords,
      durationMinutes,
      endAt,
      googleMapsApiKey,
      isHelperContext,
      numericPay,
      scheduleMode,
      selectedCareLocationAddress,
      selectedOrganization,
      selectedOrganizationLocation,
      selectedOrganizationLocationId,
      selectedOrganizationLocations.length,
      selectedServices.length,
      startAt,
    ],
  );

  const goToStep = (nextIndex) => {
    const target = Math.max(0, Math.min(nextIndex, CARE_TASK_FLOW_STEPS.length - 1));
    if (target > activeStepIndex) {
      for (let index = activeStepIndex; index < target; index += 1) {
        const error = validateStep(CARE_TASK_FLOW_STEPS[index].key);
        if (error) {
          setSubmitError(error);
          message.error(error);
          return;
        }
      }
    }
    setSubmitError("");
    setActiveStepIndex(target);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const uploadFiles = async () => {
    const selected = (photoList || []).map((entry) => entry.originFileObj).filter(Boolean);
    const uploaded = [];
    for (const file of selected) {
      const result = await uploadFile(user?.id, file, {
        maxBytes: 500_000,
        quality: 0.8,
        minQuality: 0.1,
        stepQuality: 0.1,
        preserveDimensions: true,
      });
      if (result?.fileId) uploaded.push(result.fileId);
    }
    return uploaded;
  };

  const buildPayload = async () => {
    const photoIds = await uploadFiles();
    const immediateStartAt = new Date();
    const immediateEndAt = addMinutes(immediateStartAt, durationMinutes);
    const payloadStartAt = scheduleMode === "IMMEDIATE" ? immediateStartAt : startAt;
    const payloadEndAt = scheduleMode === "IMMEDIATE" ? immediateEndAt : endAt;
    const locationForPayload = {
      address: selectedCareLocationAddress,
      latitude: selectedCareLocationCoords?.latitude,
      longitude: selectedCareLocationCoords?.longitude,
    };
    const fetchedCharges = payHelperInApp
      ? await computeTaskCharges(safeChargeBase, {
          currency: DEFAULT_CURRENCY,
          country: selectedCountry,
          state: selectedState,
          county: selectedCounty,
          city: selectedCity,
        })
      : {
          currency: DEFAULT_CURRENCY,
          units: "DOLLAR",
          country: selectedCountry,
          state: selectedState,
          county: selectedCounty,
          city: selectedCity,
          tokenAmount: safeChargeBase,
          tipAmount: 0,
          useCredits: false,
          appliedCreditsTotal: 0,
          cashPortion: safeChargeBase,
          feeTaxBase: safeChargeBase,
          totalCharge: chargeableAmount,
          chargeableAmount,
          receivableAmount,
          chargeLines: [],
          creditDeductions: [],
        };
    const fetchedFeesTotal = (fetchedCharges.chargeLines || [])
      .filter((line) => normalizeChargeText(line?.category) === "FEE")
      .reduce((sum, line) => sum + Number(line.amount || 0), 0);
    const fetchedTaxesTotal = (fetchedCharges.chargeLines || [])
      .filter((line) => normalizeChargeText(line?.category) === "TAX")
      .reduce((sum, line) => sum + Number(line.amount || 0), 0);
    const fetchedFeesAndTaxesTotal = moneyNumber(fetchedFeesTotal + fetchedTaxesTotal);
    const fetchedChargeableAmount = payHelperInApp
      ? moneyNumber(fetchedCharges.totalCharge ?? safeChargeBase + fetchedFeesAndTaxesTotal)
      : chargeableAmount;
    const taskCharges = {
      ...fetchedCharges,
      currency: fetchedCharges.currency || DEFAULT_CURRENCY,
      units: fetchedCharges.units || "DOLLAR",
      country: selectedCountry,
      state: selectedState,
      county: selectedCounty,
      city: selectedCity,
      tokenAmount: safeChargeBase,
      tipAmount: 0,
      useCredits: false,
      appliedCreditsTotal: 0,
      cashPortion: safeChargeBase,
      feeTaxBase: fetchedCharges.feeTaxBase ?? safeChargeBase,
      totalCharge: fetchedChargeableAmount,
      chargeableAmount: fetchedChargeableAmount,
      receivableAmount,
      chargeLines: fetchedCharges.chargeLines || [],
      creditDeductions: [],
    };

    const payload = {
      taskType: "CARETASK",
      serviceCategory: "CARE_SUPPORT",
      services: selectedCareServices.map((service) => ({
        id: service.id,
        name: service.title,
        briefName: service.brief,
        category: service.category,
      })),
      entity: {
        type: selectedOrganization?.type || "OTHER",
        name: selectedOrganization?.name || "",
        registeredEntityId: selectedOrganization?.id,
        organizationId: selectedOrganization?.id,
        organizationName: selectedOrganization?.name,
        organizationType: organizationLocationTypePayload(
          selectedOrganization?.organizationLocationType || selectedOrganization?.type,
          selectedOrganization?.organizationLocationTypeId,
        ),
        organizationLocation: careEntityLocationPayload(selectedOrganization?.locationInformation),
        organizationLocationId: careLocationMode === "REGISTERED" ? selectedOrganizationLocation?.id : undefined,
        organizationLocationName: careLocationMode === "REGISTERED" ? selectedOrganizationLocation?.name : undefined,
        organizationLocationType: organizationLocationTypePayload(
          selectedOrganizationLocation?.organizationLocationType || selectedOrganizationLocation?.type,
          selectedOrganizationLocation?.organizationLocationTypeId,
        ),
        organizationLocationAddress:
          careLocationMode === "REGISTERED"
            ? careEntityLocationPayload(selectedOrganizationLocation)
            : undefined,
      },
      schedule: {
        mode: scheduleMode,
        immediate: scheduleMode === "IMMEDIATE",
        startAt: payloadStartAt.toISOString(),
        endAt: payloadEndAt.toISOString(),
        durationMinutes,
      },
      compensation: {
        payType,
        payRate: numericPay,
        estimatedTotal: estimatedPay,
        currency: DEFAULT_CURRENCY,
      },
      taskCharges,
      chargeableAmount: fetchedChargeableAmount,
      receivableAmount,
      taskDetails: {
        payHelperInApp,
        feesEstimate: fetchedFeesTotal,
        taxesEstimate: fetchedTaxesTotal,
        feesAndTaxesEstimate: fetchedFeesAndTaxesTotal,
        requesterPaysEstimate: fetchedChargeableAmount,
        outOfAppPaymentFlatFee: payHelperInApp ? 0 : outOfAppPlatformFee,
        outOfAppPaymentFlatFeeCurrency: DEFAULT_CURRENCY,
      },
      location: locationForPayload,
      description: clean(watchedValues.description) || "",
      mainPhoto: photoIds[0] ?? null,
      photos: photoIds,
      workspaceId,
      requesterWorkspaceId: workspaceId,
      organizationId: effectiveOrganizationId,
    };

    return payload;
  };

  const submit = async () => {
    const firstError =
      CARE_TASK_FLOW_STEPS.map((step) => validateStep(step.key)).find(Boolean) || "";
    if (firstError) {
      setSubmitError(firstError);
      message.error(firstError);
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = await buildPayload();
      await createTaskRequest(payload, "CARE", {
        headers: {
          "O-CLIENT-APP": MOBILE_CLIENT_APP,
        },
      });
      message.success("Home Care & Assisted Living request submitted.");
      navigate("/hire-care-giver/shift-filler/posts");
    } catch (error) {
      const nextError =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong while submitting this shift request.";
      setSubmitError(nextError);
      message.error(nextError);
    } finally {
      setSubmitting(false);
    }
  };

  const renderServicesStep = () => (
    <FlowSection
      title={<RequiredLabel>Care Services Needed</RequiredLabel>}
      subtitle="Choose one or more non-medical supports."
    >
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        <Alert
          type="info"
          showIcon
          message="Caregiving support for seniors at home, adult family homes, and assisted living communities."
        />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          value={serviceQuery}
          onChange={(event) => setServiceQuery(event.target.value)}
          placeholder="Search care services"
        />
        {visibleServiceGroups.length ? (
          visibleServiceGroups.map((group) => {
            const groupSelected = group.services.every((service) => selectedServices.includes(service.id));
            const selectedCount = group.services.filter((service) => selectedServices.includes(service.id)).length;
            const expanded = Boolean(serviceQuery.trim()) || expandedServiceGroups.includes(group.title);
            return (
              <div key={group.title} className="care-task-service-group">
                <div className="care-task-service-group__head">
                  <button
                    type="button"
                    className="care-task-service-group__toggle"
                    onClick={() => toggleServiceGroup(group.title)}
                  >
                    <HeartOutlined />
                    <Text strong>{group.title}</Text>
                    {selectedCount ? <span>{selectedCount}</span> : null}
                    {expanded ? <UpOutlined /> : <DownOutlined />}
                  </button>
                  <Button type="link" onClick={() => toggleGroup(group.services)}>
                    {groupSelected ? <CheckOutlined /> : null}
                    Select all
                  </Button>
                </div>
                {expanded ? (
                  <div className="care-task-service-grid">
                    {group.services.map((service) => {
                      const selected = selectedServices.includes(service.id);
                      return (
                        <button
                          key={service.id}
                          type="button"
                          className={`care-task-service-card${selected ? " is-selected" : ""}`}
                          onClick={() => toggleService(service.id)}
                        >
                          <HeartOutlined />
                          <strong>{service.brief}</strong>
                          <span>{service.title}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })
        ) : (
          <Empty description="No care services match that search." />
        )}
        {filteredServiceGroups.length > INITIAL_VISIBLE_CARE_SERVICE_GROUPS ? (
          <Button onClick={() => setShowAllGroups((value) => !value)}>
            {showAllGroups ? "Show fewer services" : `Show ${filteredServiceGroups.length - INITIAL_VISIBLE_CARE_SERVICE_GROUPS} more groups`}
          </Button>
        ) : null}
        {selectedCareServices.length ? (
          <div className="care-task-selected-support">
            <button
              type="button"
              className="care-task-selected-support__head"
              onClick={() => setSelectedSupportExpanded((value) => !value)}
            >
              <Text strong>Selected support</Text>
              <span>
                <Button size="small" type="link" onClick={(event) => {
                  event.stopPropagation();
                  clearSelectedServices();
                }}>
                  Clear all
                </Button>
                <strong>{selectedCareServices.length}</strong>
                {selectedSupportExpanded ? <UpOutlined /> : <DownOutlined />}
              </span>
            </button>
            {selectedSupportExpanded ? (
              <div className="care-task-selected-support__list">
                {selectedCareServices.map((service) => (
                  <div key={service.id} className="care-task-selected-support__item">
                    <span>
                      <strong>{service.brief}</strong>
                      <Text>{service.description}</Text>
                    </span>
                    <Button
                      shape="circle"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() => toggleService(service.id)}
                    />
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </Space>
    </FlowSection>
  );

  const renderDescriptionStep = () => (
    <FlowSection title="Shift description">
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        {showPhotoPrivacyWarning ? (
          <div className="care-task-photo-warning">
            <WarningOutlined />
            <span>
              <Text strong>Photo privacy reminder</Text>
              <Text>
                Do not upload photos showing people, faces, personal identification, medical records, addresses, or other private information.
              </Text>
            </span>
            <Button shape="circle" size="small" icon={<CloseOutlined />} onClick={() => setShowPhotoPrivacyWarning(false)} />
          </div>
        ) : null}
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={7} placeholder="Describe the care shift, routines, preferences, safety notes, parking details, or visit instructions." />
        </Form.Item>
        <Upload
          listType="picture-card"
          fileList={photoList}
          beforeUpload={() => false}
          onChange={({ fileList }) => setPhotoList(fileList)}
          accept="image/*"
        >
          <div>
            <CameraOutlined />
            <div style={{ marginTop: 8 }}>Photos</div>
          </div>
        </Upload>
      </Space>
    </FlowSection>
  );

  const renderLocationStep = () => (
    <FlowSection
      title="Organization and location"
    >
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        {!canRequestCareHelp ? (
          <Alert
            type="warning"
            showIcon
            message="Organization requester workspace required"
            description="Switch to a care facility workspace before creating a Home Care & Assisted Living request."
          />
        ) : null}
        <div className="care-task-entity-picker">
          <div className="care-task-entity-picker__head">
            <span>
              <Text strong>Use selected organization</Text>
              <Text type="secondary">Uses the organization selected in your current app context.</Text>
            </span>
            {organizationsLoading ? <Text type="secondary">Loading...</Text> : null}
          </div>
          {!organizationsLoading && organizations.length === 0 ? (
            <Text type="secondary">No organization is selected for your current context.</Text>
          ) : (
            <div className="care-task-org-list">
              {organizations.map((organization) => {
                const selected = String(selectedOrganizationId) === String(organization.id);
                const location = organization.locationInformation || {};
                const locationLabel = [location.city, location.state, location.country].filter(Boolean).join(", ");
                return (
                  <button
                    key={organization.id || organization.name}
                    type="button"
                    className={`care-task-org-option${selected ? " is-selected" : ""}`}
                    onClick={() => setSelectedOrganizationId(organization.id)}
                  >
                    <span className="care-task-org-option__icon">{selected ? <CheckOutlined /> : <HomeOutlined />}</span>
                    <span>
                      <Text strong>{organization.name || organization.id}</Text>
                      <Text type="secondary">
                        {formatEntityType(organization.type)}
                        {locationLabel ? ` - ${locationLabel}` : ""}
                      </Text>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {selectedOrganization ? (
          <div className="care-task-location-picker">
            <div className="care-task-entity-picker__head">
              <span>
                <Text strong>Care location</Text>
                <Text type="secondary">Use a registered organization location or enter one manually.</Text>
              </span>
            </div>
            <div className="care-task-segment-row">
              <button
                type="button"
                className={`care-task-segment${careLocationMode === "REGISTERED" ? " is-selected" : ""}`}
                onClick={() => setCareLocationMode("REGISTERED")}
              >
                <HomeOutlined /> Registered
              </button>
              <button
                type="button"
                className={`care-task-segment${careLocationMode === "MANUAL" ? " is-selected" : ""}`}
                onClick={() => setCareLocationMode("MANUAL")}
              >
                <SearchOutlined /> Enter manually
              </button>
            </div>
          </div>
        ) : null}

        {careLocationMode === "REGISTERED" ? (
          selectedOrganizationLocations.length === 0 ? (
            <div className="care-task-location-empty">
              <Text type="secondary">No organization locations are attached to this entity.</Text>
            </div>
          ) : (
            <div className="care-task-org-list">
              {selectedOrganizationLocations.map((location) => {
                const selected = String(selectedOrganizationLocationId) === String(location.id);
                const address = addressFromLocationInfo(location);
                const hasFullAddress = hasStreetAddress(location);
                const serviceCount = location.services?.length || location.serviceOfferings?.length || 0;
                return (
                  <button
                    key={location.id || location.name}
                    type="button"
                    className={`care-task-org-option care-task-location-option${selected ? " is-selected" : ""}`}
                    onClick={() => {
                      setSelectedOrganizationLocationId(location.id);
                      setCoords(coordsFromLocationInfo(location));
                    }}
                  >
                    <span className="care-task-org-option__icon">{selected ? <CheckOutlined /> : <HomeOutlined />}</span>
                    <span>
                      <Text strong>{location.name || "Organization location"}</Text>
                      <Text type="secondary">
                        {location.status || "ACTIVE"}
                        {serviceCount ? ` - ${serviceCount} services` : ""}
                      </Text>
                      <Text type="secondary">Type: {formatEntityType(location.organizationLocationType || location.type)}</Text>
                      <Text type={hasFullAddress ? undefined : "danger"}>
                        {hasFullAddress ? address : "Full street address not set"}
                      </Text>
                    </span>
                  </button>
                );
              })}
            </div>
          )
        ) : (
          <>
            {appConfigLoading && !googleMapsApiKey ? <Text type="secondary">Loading Google Maps configuration...</Text> : null}
            <GooglePlacesAutocompleteInput
              googleMapsApiKey={googleMapsApiKey}
              placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Enter address"}
              value={locationInput}
              onChange={(nextValue) => {
                setLocationInput(nextValue);
                setManualLocationParts({});
                if (!googleMapsApiKey) setCoords(null);
              }}
              onResolvePlace={({ details, prediction }) => {
                const fields = details ? addressFieldsFromPlaceDetails(details) : {};
                const formattedAddress = details?.formatted_address || prediction?.description || locationInput || "";
                setLocationInput(formattedAddress);
                setManualLocationParts({
                  country: fields.country || "US",
                  state: fields.state || "",
                  county: fields.county || "",
                  city: fields.city || "",
                });
                if (Number.isFinite(fields.latitude) && Number.isFinite(fields.longitude)) {
                  setCoords({ latitude: fields.latitude, longitude: fields.longitude });
                }
              }}
            />
          </>
        )}
        <div className="care-task-location-summary">
          <SummaryRow label="Facility" value={selectedOrganization?.name} />
          <SummaryRow label="Email" value={organizationContactEmail(selectedOrganization)} />
          <SummaryRow label="Phone" value={organizationContactPhone(selectedOrganization)} />
          <SummaryRow label="Care address" value={selectedCareLocationAddress} strong />
        </div>
      </Space>
    </FlowSection>
  );

  const renderSchedulePayStep = () => (
    <FlowSection title="Schedule and pay">
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        <Form.Item name="scheduleMode" label="When do you need help?">
          <Radio.Group optionType="button" buttonStyle="solid">
            <Radio.Button value="IMMEDIATE">Immediate</Radio.Button>
            <Radio.Button value="SCHEDULED">Scheduled</Radio.Button>
          </Radio.Group>
        </Form.Item>
        {scheduleMode === "SCHEDULED" ? (
          <>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <Form.Item name="startAt" label="Start">
                  <DatePicker showTime={{ format: "HH:mm" }} format="MMM D, YYYY HH:mm" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="endAt" label="End">
                  <DatePicker showTime={{ format: "HH:mm" }} format="MMM D, YYYY HH:mm" style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
            {scheduledRangeInvalid ? (
              <Alert
                type="warning"
                showIcon
                message="Start must be before end"
                description="Choose an end time after the start time."
              />
            ) : (
              <Alert
                type="info"
                showIcon
                message={`Shift duration: ${formatDurationText(scheduledDurationMinutes)}`}
              />
            )}
          </>
        ) : (
          <>
            <div className="care-task-immediate-box">
              <WarningOutlined />
              <Text>
                This request starts as soon as a Care Giver accepts it. Add the estimated duration so Care Givers see the expected time.
              </Text>
            </div>
            <Text strong>What is the shift duration?</Text>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <Form.Item name="durationHours" label="Hours">
                  <InputNumber min={0} max={24} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="durationMinutes" label="Minutes">
                  <InputNumber min={0} max={59} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>
          </>
        )}
        {durationMinutes > 0 ? (
          <Text className="care-task-wage-suggestion">
            {minimumWageLabel} is {formatMoney(minimumWageRate)}/hr. We recommend {payType === "HOURLY" ? "setting an hourly rate above " : "offering more than "}
            <strong>{payType === "HOURLY" ? `${formatMoney(minimumWageRate)}/hr` : formatMoney(suggestedAmount)}</strong>
            {` for ${formatDurationText(durationMinutes)}.`}
          </Text>
        ) : null}
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <Form.Item name="payType" label="Pay type">
              <Select
                onChange={() => form.setFieldValue("payRate", undefined)}
                options={[
                  { value: "HOURLY", label: "Hourly" },
                  { value: "LUMP_SUM", label: "Lump sum" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="payRate" label={payType === "LUMP_SUM" ? "Amount" : "Hourly rate"}>
              <InputNumber min={0} prefix="$" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
        <div className="care-task-suggestion-card">
          <SummaryRow label="Duration" value={durationHours > 0 ? `${durationHours.toFixed(durationHours >= 10 ? 0 : 1)} hours` : "Not set"} />
          <SummaryRow label="Estimated pay" value={formatMoney(estimatedPay)} strong />
          <SummaryRow label="Estimated Fees & Taxes" value={formatMoney(feesTotal + taxesTotal)} />
          <SummaryRow label="Requester pays" value={formatMoney(chargeableAmount)} strong />
          {scheduleMode === "SCHEDULED" && payType === "LUMP_SUM" ? (
            <SummaryRow label="Average per hour" value={`${formatMoney(averageHourlyPay)}/hr`} />
          ) : null}
        </div>
      </Space>
    </FlowSection>
  );

  const renderPaymentChoiceStep = () => (
    <FlowSection title="Care Giver Payment" subtitle="Choose whether the Care Giver will be paid through Olitask.">
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        <div className="care-task-choice-grid">
          <button
            type="button"
            className={`care-task-payment-choice${payHelperInApp ? " is-selected" : ""}`}
            onClick={() => setPayHelperInApp(true)}
          >
            <CreditCardOutlined />
            <strong>Pay Care Giver in the app</strong>
            <span>Olitask handles the Care Giver payment for this care shift.</span>
            {payHelperInApp ? <CheckOutlined /> : null}
          </button>
          <button
            type="button"
            className={`care-task-payment-choice${!payHelperInApp ? " is-selected" : ""}`}
            onClick={() => setPayHelperInApp(false)}
          >
            <WalletOutlined />
            <strong>Do not pay Care Giver in the app</strong>
            <span>
              {hasTaskFeeWaiver
                ? "Your workspace subscription waives the outside-app platform fee."
                : `You will be charged a flat ${formatMoney(outOfAppPlatformFee)} platform fee plus estimated taxes and fees.`}
            </span>
            {!payHelperInApp ? <CheckOutlined /> : null}
          </button>
        </div>
        <div className="care-task-suggestion-card">
          <SummaryRow label={payHelperInApp ? "Shift offer amount" : "Flat platform fee"} value={formatMoney(safeChargeBase)} />
          <SummaryRow label="Estimated Fees & Taxes" value={formatMoney(feesTotal + taxesTotal)} />
          <SummaryRow label="Requester pays" value={formatMoney(chargeableAmount)} strong />
        </div>
      </Space>
    </FlowSection>
  );

  const renderReviewStep = () => (
    <div className="care-task-review-card">
      <div className="care-task-review-hero">
        <div className="care-task-review-icon"><HeartOutlined /></div>
        <span>
          <Text className="care-task-review-title">Confirm Request</Text>
          <Text className="care-task-review-subtitle">Make sure the care shift details look right.</Text>
        </span>
      </div>

      <div className="care-task-review-section">
        <div className="care-task-review-section__head">
          <HeartOutlined />
          <Text strong>Care details</Text>
        </div>
        <SummaryRow label="Care services" value={servicesSummary} />
        <SummaryRow label="Shift description" value={watchedValues.description?.trim() || "Not provided"} />
        <SummaryRow label="Photos" value={photoSummary} />
      </div>

      <div className="care-task-review-section">
        <div className="care-task-review-section__head">
          <HomeOutlined />
          <Text strong>Place and time</Text>
        </div>
        <SummaryRow label="Location" value={selectedCareLocationAddress || "Not set"} />
        <SummaryRow label="Schedule" value={scheduleSummary} />
        <SummaryRow label="Duration" value={formatDurationText(durationMinutes)} />
      </div>

      <div className="care-task-review-section is-payment">
        <div className="care-task-review-section__head">
          <WalletOutlined />
          <Text strong>Payment</Text>
        </div>
        <SummaryRow label="Pay type" value={payType === "HOURLY" ? "Hourly" : "Lump sum"} />
        <SummaryRow
          label={payType === "HOURLY" ? "Hourly rate" : "Shift offer"}
          value={formatMoney(numericPay)}
        />
        <SummaryRow label="Care Giver Payment" value={payHelperInApp ? "Paid through app" : "Paid outside app"} />
        {!payHelperInApp ? <SummaryRow label="Flat platform fee" value={formatMoney(outOfAppPlatformFee)} /> : null}
        <SummaryRow label="Estimated Fees & Taxes" value={formatMoney(feesTotal + taxesTotal)} />
        <div className="care-task-review-total">
          <Text>Requester pays</Text>
          <strong>{formatMoney(chargeableAmount)}</strong>
        </div>
      </div>
    </div>
  );

  const renderFlowHeader = () => (
    <div className="care-task-mobile-flow-header">
      <Text className="care-task-flow-eyebrow">
        Step {activeStepIndex + 1} of {CARE_TASK_FLOW_STEPS.length}
      </Text>
      <Text className="care-task-flow-title">{activeStep.title}</Text>
      <div className="care-task-flow-dots">
        {CARE_TASK_FLOW_STEPS.map((step, index) => (
          <button
            key={step.key}
            type="button"
            aria-label={step.title}
            className={`care-task-flow-dot${index === activeStepIndex ? " is-active" : ""}${index < activeStepIndex ? " is-done" : ""}`}
            onClick={() => goToStep(index)}
            disabled={index > activeStepIndex + 1}
          />
        ))}
      </div>
    </div>
  );

  const renderIntro = () => (
    <>
      <div className="care-task-explanation-badge">
        <InfoCircleOutlined />
        <span>Caregiving support for seniors at home, in adult family homes, and assisted living communities.</span>
      </div>
      {showIntroBadge ? (
        <div className="care-task-mobile-hero">
          <div className="care-task-mobile-hero__icon"><HeartOutlined /></div>
          <span>
            <Text className="care-task-mobile-hero__title">Non-medical care support</Text>
            <Text className="care-task-mobile-hero__subtitle">
              Request elderly care support, special needs support, nursing home visits, or non-medical help at home.
            </Text>
          </span>
          <Button shape="circle" size="small" icon={<CloseOutlined />} onClick={() => setShowIntroBadge(false)} />
        </div>
      ) : null}
      {showCareWarningBadge ? (
        <div className="care-task-warning-badge">
          <WarningOutlined />
          <span>Home Care & Assisted Living Care Givers do not provide medical treatment or administer medication.</span>
          <Button shape="circle" size="small" icon={<CloseOutlined />} onClick={() => setShowCareWarningBadge(false)} />
        </div>
      ) : null}
    </>
  );

  const renderActiveStep = () => {
    switch (activeStep.key) {
      case "services":
        return renderServicesStep();
      case "description":
        return renderDescriptionStep();
      case "location":
        return renderLocationStep();
      case "schedulePay":
        return renderSchedulePayStep();
      case "paymentChoice":
        return renderPaymentChoiceStep();
      case "review":
      default:
        return renderReviewStep();
    }
  };

  return (
    <div className="olitask-mobile-flow is-care shift-filler-page">
      <div className="olitask-mobile-shell">
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          <div className="care-task-mobile-screen-header">
            <Button
              shape="circle"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/hire-care-giver/shift-filler/posts")}
            />
            <div>
              <Title level={2} className="care-task-mobile-screen-title">Home Care & Assisted Living Request</Title>
              <Text className="care-task-mobile-screen-subtitle">
                Create a non-medical care request from your current organization workspace.
              </Text>
            </div>
            <Button className="olitask-hero-action" onClick={() => navigate("/hire-care-giver/shift-filler/posts")}>
              View Posts
            </Button>
          </div>
          {renderIntro()}

          {submitError ? <Alert showIcon type="error" message="Review needed" description={submitError} /> : null}
          {isHelperContext ? (
            <Alert
              showIcon
              type="warning"
              message="Requester context required"
              description="Care Giver context is for finding and working on requests. Switch to Requester context to submit a new care shift request."
            />
          ) : null}

          <Form
            form={form}
            layout="vertical"
            initialValues={{
              durationMinutes: 0,
              startAt: dayjs().add(2, "hour"),
              endAt: dayjs().add(4, "hour"),
              scheduleMode: "IMMEDIATE",
              payType: "HOURLY",
            }}
            requiredMark={false}
          >
            <Card className="olitask-mobile-card care-task-flow-shell" bodyStyle={{ padding: 14 }}>
              {renderFlowHeader()}
              {renderActiveStep()}
              <div className="olitask-submit-bar">
                <Button icon={<ArrowLeftOutlined />} onClick={() => goToStep(activeStepIndex - 1)} disabled={activeStepIndex === 0}>
                  Back
                </Button>
                {isReviewStep ? (
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={submitting || uploadingFile}
                    disabled={isHelperContext}
                    onClick={submit}
                  >
                    Confirm & Submit
                  </Button>
                ) : (
                  <Button type="primary" icon={<RightOutlined />} onClick={() => goToStep(activeStepIndex + 1)}>
                    Next
                  </Button>
                )}
              </div>
            </Card>
          </Form>
        </Space>
      </div>
    </div>
  );
}
